import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { supabase } from "../../../lib/supabaseClient";
import { Link } from "react-router-dom";
import AnimatedSection from "../../Components/AnimatedSection";
import PropertiesContainer from '../../Components/Worldmap/PropertiesContainer';
import ImageSlider from '../../Components/Worldmap/ImageSlider';
import * as api from '../../../api';
import "./MapComponent.css";
import { getAllDistricts, propertyBelongsToDistrict } from '../../Utils/districtUtils';

// Import all your icons
import retailIcon from './SVG/1_CHARIOT.png';
import shoppingIcon from './SVG/2_SHOPPING.png';
import hotelIcon from './SVG/3_SONETTE.png';
import officeIcon from './SVG/8_OFFICE_STROKE.png';
import restaurantIcon from './SVG/5_CUISINE.png';
import culturalIcon from './SVG/6_PEINTURE.png';
import residenceIcon from './SVG/7_SERRURE.png';
import urbanIcon from './SVG/8_BANC.png';
import clusterIcon from './SVG/logo16.png';
import architectureIcon from './SVG/4_OFFICE.png';
import installationIcon from './SVG/9_INSTALLATION_SCENOGRAPHY.png';
import FD1 from './SVG/9_ROUGE.png';
import FD2 from './SVG/10_DEGRADE.png';
import FD3 from './SVG/11_POINTILLE2024.png';
import FD4 from './AwardsIcons/2025/2025_empty.png';
import city from './SVG/city3.png';
import city_card from './SVG/city_horizontal.png';
import city2026 from './SVG/city_2026.png';
import city2026_card from './SVG/city_2026_horizontal.png';
import ouiDesignIcon from './SVG/oui_design.png';
import ouiDesign_card from './SVG/oui_design_horizontal.webp';
import parisDesignWeekIcon from './SVG/paris_design_week.png';
import parisDesignWeek_card from './SVG/paris_design_week_horizontal.png';

// Award icons imports
import Retail2019Icon from './AwardsIcons/2019/2019_shopping.png';
import Retail2022Icon from './AwardsIcons/2022/2022_shopping.png';
import Retail2024Icon from './AwardsIcons/2024/2024_shopping.png';
import Hotel2019Icon from './AwardsIcons/2019/2019_hotel.png';
import Hotel2022Icon from './AwardsIcons/2022/2022_hotel.png';
import Hotel2024Icon from './AwardsIcons/2024/2024_hotel.png';
import Cultural2019Icon from './AwardsIcons/2019/2019_creative.png';
import Cultural2022Icon from './AwardsIcons/2022/2022_creative.png';
import Cultural2024Icon from './AwardsIcons/2024/2024_creative.png';
import Private2019Icon from './AwardsIcons/2019/2019_private.png';
import Private2022Icon from './AwardsIcons/2022/2022_private.png';
import Private2024Icon from './AwardsIcons/2024/2024_private.png';
import Restaurant2019Icon from './AwardsIcons/2019/2019_restaurant.png';
import Restaurant2022Icon from './AwardsIcons/2022/2022_restaurant.png';
import Restaurant2024Icon from './AwardsIcons/2024/2024_restaurant.png';
import Urban2019Icon from './AwardsIcons/2019/2019_urban.png';
import Urban2022Icon from './AwardsIcons/2022/2022_urban.png';
import Urban2024Icon from './AwardsIcons/2024/2024_urban.png';
import Architecture2019Icon from './AwardsIcons/2019/2019_architecture.png';
import Architecture2022Icon from './AwardsIcons/2022/2022_architecture.png';
import Architecture2024Icon from './AwardsIcons/2024/2024_architecture.png';
import Office2019Icon from './AwardsIcons/2019/2019_office.png';
import Office2022Icon from './AwardsIcons/2022/2022_office.png';
import Office2024Icon from './AwardsIcons/2024/2024_office.png';

// 2025 Award icons imports
import Retail2025Icon from './AwardsIcons/2025/2025_shopping.png';
import Hotel2025Icon from './AwardsIcons/2025/2025_hotel.png';
import Cultural2025Icon from './AwardsIcons/2025/2025_creative.png';
import Private2025Icon from './AwardsIcons/2025/2025_private.png';
import Restaurant2025Icon from './AwardsIcons/2025/2025_restaurant.png';
import Urban2025Icon from './AwardsIcons/2025/2025_urban.png';
import Architecture2025Icon from './AwardsIcons/2025/2025_architecture.png';
import Office2025Icon from './AwardsIcons/2025/2025_office.png';
import Installation2025Icon from './SVG/2025_cube.png';

// eslint-disable-next-line import/no-webpack-loader-syntax
mapboxgl.workerClass = require("worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker").default;

const POINTS_OF_INTEREST_OPTIONS = [
    "Retail", "Hotel", "Office", "Restaurant/bar",
    "Cultural space", "Private residence", "Urban space/Transportation", "Architecture", "Installation/Scenography"
];

// Helper function to format category names for display
const formatCategoryForDisplay = (category) => {
    if (category === "Installation/Scenography") {
        return "Installation / Scenography";
    }
    return category;
};

const AWARDS_OPTIONS = [
    "Le FD100 - 1ère édition", "Le FD100 - 2ème édition", "Le FD100 - 3ème édition", "Le FD100 - 4ème édition"
];

const DESIGN_EVENTS_OPTIONS = [
    "In The City 2026",
    "Oui Design! 2025",
    "Paris Design Week 2025"
];

const DISTRICTS_OPTIONS = getAllDistricts().map(d => d.name);

const DESIGN_ICONS = {
    "ITC": city,
    "ITC26": city2026,
    "OUI": ouiDesignIcon,
    "PDW": parisDesignWeekIcon
};

const DESIGN_POINTS_OF_SALES_OPTIONS = [
    "Art de Vivre", "Atelier Bam Design", "Atelier George", "Bernardaud", "Bibelo",
    "Bruno Moinard Editions", "Coedition", "Collectional", "Cuisines Morel", "Devialet",
    "Drugeot", "Fermob", "Forestier", "Gautier", "Habitat", "Henryot & Cie 1867", "Ibride",
    "Kann Design", "La Chance", "La Manufacture", "Lafuma Mobilier", "Le Point D", "Lelièvre",
    "Liaigre", "Maison Dada", "Maison Pouenat", "Manufacture de Février", 
    "Manufacture de Tapis de Bourgogne", "Manufacture des Emaux De Longwy 1798", 
    "Moissonnier", "NOMA", "Par Excellence", "Petite Friture", "Philippe Hurel", 
    "Plumbum", "Red Edition", "Roche Bobois", "Semeur d'Etoiles", "Smarin", "Stamp", "Tolix"
];

const EVENT_TYPE_ICONS = {
    "Exhibition": culturalIcon,
    "Pop-up exhibition": culturalIcon,
    "Panel discussion": culturalIcon,
    "Talk": culturalIcon,
    "Open House": residenceIcon,
    "Gallery Tour": culturalIcon,
    "Opening Reception": culturalIcon,
    "Cocktail Reception": restaurantIcon,
    "Breakfast": restaurantIcon,
    "Special Event": culturalIcon,
    "Presentation": culturalIcon,
    "Gallery Walkthrough": culturalIcon,
    "Private Visit": residenceIcon,
    "Open Showroom": retailIcon,
    "Guided Tour": urbanIcon
};

const POINT_ICONS = {
    "Retail": shoppingIcon,
    "Hotel": hotelIcon,
    "Office": officeIcon,
    "Restaurant/bar": restaurantIcon,
    "Cultural space": culturalIcon,
    "Private residence": residenceIcon,
    "Urban space/Transportation": urbanIcon,
    "Architecture": architectureIcon,
    "Installation/Scenography": installationIcon
};

const AWARD_ICONS = {
    "FD2019": FD1,
    "FD2022": FD2,
    "FD2024": FD3,
    "FD2025": FD4,
};

const AWARDS_ICONS = {
    "Retail-2019": Retail2019Icon,
    "Retail-2022": Retail2022Icon,
    "Retail-2024": Retail2024Icon,
    "Hotel-2019": Hotel2019Icon,
    "Hotel-2022": Hotel2022Icon,
    "Hotel-2024": Hotel2024Icon,
    "Cultural space-2019": Cultural2019Icon,
    "Cultural space-2022": Cultural2022Icon,
    "Cultural space-2024": Cultural2024Icon,
    "Private residence-2019": Private2019Icon,
    "Private residence-2022": Private2022Icon,
    "Private residence-2024": Private2024Icon,
    "Restaurant/bar-2019": Restaurant2019Icon,
    "Restaurant/bar-2022": Restaurant2022Icon,
    "Restaurant/bar-2024": Restaurant2024Icon,
    "Urban space/Transportation-2019": Urban2019Icon,
    "Urban space/Transportation-2022": Urban2022Icon,
    "Urban space/Transportation-2024": Urban2024Icon,
    "Architecture-2019": Architecture2019Icon,
    "Architecture-2022": Architecture2022Icon,
    "Architecture-2024": Architecture2024Icon,
    "Office-2019": Office2019Icon,
    "Office-2022": Office2022Icon,
    "Office-2024": Office2024Icon,
    "Installation/Scenography-2019": installationIcon,
    "Installation/Scenography-2022": installationIcon,
    "Installation/Scenography-2024": installationIcon,
    "Retail-2025": Retail2025Icon,
    "Hotel-2025": Hotel2025Icon,
    "Cultural space-2025": Cultural2025Icon,
    "Private residence-2025": Private2025Icon,
    "Restaurant/bar-2025": Restaurant2025Icon,
    "Urban space/Transportation-2025": Urban2025Icon,
    "Architecture-2025": Architecture2025Icon,
    "Office-2025": Office2025Icon,
    "Installation/Scenography-2025": Installation2025Icon
};

const SALES_ICONS = {
    "Art de Vivre": retailIcon,
    "Atelier Bam Design": retailIcon,
    "Atelier George": retailIcon,
    "Bernardaud": retailIcon,
    "Bibelo": retailIcon,
    "Bruno Moinard Editions": retailIcon,
    "Coedition": retailIcon,
    "Collectional": retailIcon,
    "Cuisines Morel": retailIcon,
    "Devialet": retailIcon,
    "Drugeot": retailIcon,
    "Fermob": retailIcon,
    "Forestier": retailIcon,
    "Gautier": retailIcon,
    "Habitat": retailIcon,
    "Henryot & Cie 1867": retailIcon,
    "Ibride": retailIcon,
    "Kann Design": retailIcon,
    "La Chance": retailIcon,
    "La Manufacture": retailIcon,
    "Lafuma Mobilier": retailIcon,
    "Le Point D": retailIcon,
    "Lelièvre": retailIcon,
    "Liaigre": retailIcon,
    "Maison Dada": retailIcon,
    "Maison Pouenat": retailIcon,
    "Manufacture de Février": retailIcon,
    "Manufacture de Tapis de Bourgogne": retailIcon,
    "Manufacture des Emaux De Longwy 1798": retailIcon,
    "Moissonnier": retailIcon,
    "NOMA": retailIcon,
    "Par Excellence": retailIcon,
    "Petite Friture": retailIcon,
    "Philippe Hurel": retailIcon,
    "Plumbum": retailIcon,
    "Red Edition": retailIcon,
    "Roche Bobois": retailIcon,
    "Semeur d'Etoiles": retailIcon,
    "Smarin": retailIcon,
    "Stamp": retailIcon,
    "Tolix": retailIcon
};

// URL parameter handling functions
const handleUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const filterType = urlParams.get('filter');
  const filterValue = urlParams.get('value');
  const lat = parseFloat(urlParams.get('lat'));
  const lng = parseFloat(urlParams.get('lng'));
  const zoom = parseInt(urlParams.get('zoom'));
  
  return {
    filterType,
    filterValue,
    mapView: {
      lat: isNaN(lat) ? null : lat,
      lng: isNaN(lng) ? null : lng,
      zoom: isNaN(zoom) ? null : zoom
    }
  };
};

// Function to generate a share link
const generateShareLink = (filterType, filterValue, mapView) => {
  const baseUrl = 'https://connectedby.lefrenchdesign.org/worldmap';
  const params = new URLSearchParams();
  
  if (filterType && filterValue) {
    params.set('filter', filterType);
    params.set('value', filterValue);
  }
  
  if (mapView) {
    if (mapView.lat) params.set('lat', mapView.lat.toFixed(6));
    if (mapView.lng) params.set('lng', mapView.lng.toFixed(6));
    if (mapView.zoom) params.set('zoom', Math.round(mapView.zoom));
  }
  
  const paramString = params.toString();
  return paramString ? `${baseUrl}?${paramString}` : baseUrl;
};

// Function to create a preset share link for Oui Design events
const createOuiDesignShareLink = () => {
  // New York coordinates (center of where most Oui Design events are)
  const nyCoordinates = {
    lat: 40.7306,
    lng: -73.9867,
    zoom: 12
  };
  
  const shareLink = generateShareLink('designEvent', 'Oui Design! 2025', nyCoordinates);
  console.log('Created Oui Design share link:', shareLink);
  
  return shareLink;
};

const SearchBar = ({ onSearch, geoJsonData }) => {
    const [searchValue, setSearchValue] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 80, left: 0, width: 600 });
    const searchRef = useRef(null);
    const suggestionsRef = useRef(null);

    // Update dropdown position
    const updateDropdownPosition = () => {
        if (searchRef.current) {
            const rect = searchRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + 2,
                left: rect.left,
                width: rect.width
            });
        }
    };

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target) &&
                suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };

        const handleScroll = () => {
            if (showSuggestions) {
                updateDropdownPosition();
            }
        };

        const handleResize = () => {
            if (showSuggestions) {
                updateDropdownPosition();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', handleScroll, true);
        window.addEventListener('resize', handleResize);
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('resize', handleResize);
        };
    }, [showSuggestions]);

    const handleSearchChange = (value) => {
        setSearchValue(value);
        onSearch(value);
        setSelectedIndex(-1);

        if (value.trim() && geoJsonData) {
            const lowercaseQuery = value.toLowerCase();
            
            // Score and filter features based on priority fields
            const scoredFeatures = geoJsonData.features
                .map(feature => {
                    const properties = feature.properties;
                    let score = 0;
                    let matched = false;
                    
                    // Priority 1: nom_etablissement (highest priority)
                    if (properties.nom_etablissement && 
                        properties.nom_etablissement.toString().toLowerCase().includes(lowercaseQuery)) {
                        score += 100;
                        matched = true;
                    }
                    
                    // Priority 2: titre_affiche
                    if (properties.titre_affiche && 
                        properties.titre_affiche.toString().toLowerCase().includes(lowercaseQuery)) {
                        score += 50;
                        matched = true;
                    }
                    
                    // Priority 3: design_points_of_sales
                    if (properties.design_points_of_sales && 
                        properties.design_points_of_sales.toString().toLowerCase().includes(lowercaseQuery)) {
                        score += 25;
                        matched = true;
                    }
                    
                    // Priority 4: Other fields (lower priority)
                    if (!matched) {
                        const otherFieldsMatch = Object.entries(properties).some(([key, val]) => {
                            if (key !== 'nom_etablissement' && key !== 'titre_affiche' && 
                                key !== 'design_points_of_sales' && val && 
                                val.toString().toLowerCase().includes(lowercaseQuery)) {
                                return true;
                            }
                            return false;
                        });
                        if (otherFieldsMatch) {
                            score += 1;
                            matched = true;
                        }
                    }
                    
                    return { feature, score, matched };
                })
                .filter(item => item.matched)
                .sort((a, b) => b.score - a.score)
                .slice(0, 20) // Limit to 20 suggestions
                .map(item => item.feature);

            // Format suggestions
            const formattedSuggestions = scoredFeatures.map(feature => {
                const props = feature.properties;
                let title = props.nom_etablissement || props.titre_affiche || 'Unknown Project';
                let subtitle = '';
                let imageUrl = '';
                
                if (props.projectType === 'itc') {
                    subtitle = 'In the City';
                    imageUrl = city_card;
                } else if (props.projectType === 'itc26') {
                    subtitle = 'In The City 2026';
                    imageUrl = city2026_card;
                } else if (props.projectType === 'oui') {
                    subtitle = 'Oui Design! 2025';
                    imageUrl = ouiDesign_card;
                } else if (props.projectType === 'pdw') {
                    subtitle = 'Paris Design Week 2025';
                    imageUrl = parisDesignWeek_card;
                } else if (props.projectType === 'pos') {
                    subtitle = `Point of Sale - ${props.design_points_of_sales || ''}`;
                    imageUrl = props.photos_projet ? props.photos_projet.split(',')[0].trim() : retailIcon;
                } else if (props.points_of_interest) {
                    subtitle = props.points_of_interest;
                    if (props.fiche_agence) {
                        subtitle += ` - ${props.fiche_agence}`;
                    }
                    imageUrl = props.photos_projet ? props.photos_projet.split(',')[0].trim() : '';
                }
                
                return {
                    feature: feature,
                    title: title,
                    subtitle: subtitle,
                    address: props.adresse || '',
                    imageUrl: imageUrl
                };
            });

            setSuggestions(formattedSuggestions);
            setShowSuggestions(formattedSuggestions.length > 0);
            if (formattedSuggestions.length > 0) {
                updateDropdownPosition();
            }
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }
    };

    const handleSuggestionClick = (suggestion) => {
        setSearchValue(suggestion.title);
        onSearch(suggestion.title);
        setSuggestions([]);
        setShowSuggestions(false);
        
        // Optionally trigger a click on the map marker
        if (window.map && window.map.current) {
            const coordinates = suggestion.feature.geometry.coordinates;
            window.map.current.flyTo({
                center: coordinates,
                zoom: 15,
                essential: true,
                duration: 1000
            });
        }
    };

    const handleKeyDown = (e) => {
        if (!showSuggestions) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(prev => 
                prev < suggestions.length - 1 ? prev + 1 : prev
            );
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        } else if (e.key === 'Enter' && selectedIndex >= 0) {
            e.preventDefault();
            handleSuggestionClick(suggestions[selectedIndex]);
        } else if (e.key === 'Escape') {
            setShowSuggestions(false);
            setSelectedIndex(-1);
        }
    };

    // Scroll selected suggestion into view
    useEffect(() => {
        if (selectedIndex >= 0 && suggestionsRef.current) {
            const selectedElement = suggestionsRef.current.children[selectedIndex];
            if (selectedElement) {
                selectedElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest'
                });
            }
        }
    }, [selectedIndex]);

    return (
        <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderBottom: '1px solid #eee',
            position: 'relative',
            zIndex: 1000,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
        }}>
            <div style={{ display: 'flex', alignItems: 'center', width: '100%', marginBottom: '10px' }}>
                <Link to='/annuaire' style={{ marginRight: '15px' }}>
                    <img style={{width: '120px'}} src='/img/LogosV3.png' alt='Le FRENCH DESIGN'/>
                </Link>

                <div ref={searchRef} style={{ position: 'relative', width: '100%', maxWidth: '600px', zIndex: 10000 }}>
                    <input
                        type="text"
                        placeholder="Search projects..."
                        value={searchValue}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        onKeyDown={handleKeyDown}
                        style={{
                            width: '100%',
                            padding: '12px 20px',
                            fontSize: '16px',
                            border: '1px solid black',
                            borderRadius: '4px',
                            height: '50px',
                            display: 'block',
                            position: 'relative',
                            zIndex: 10001,
                        }}
                    />
                    
                    {showSuggestions && ReactDOM.createPortal(
                        <div 
                            ref={suggestionsRef}
                            style={{
                                position: 'fixed',
                                top: `${dropdownPosition.top}px`,
                                left: `${dropdownPosition.left}px`,
                                width: `${dropdownPosition.width}px`,
                                backgroundColor: 'white',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                maxHeight: '300px',
                                overflowY: 'auto',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                zIndex: 999999,
                            }}
                        >
                            {suggestions.map((suggestion, index) => (
                                <div
                                    key={index}
                                    onClick={() => handleSuggestionClick(suggestion)}
                                    onMouseEnter={() => setSelectedIndex(index)}
                                    style={{
                                        padding: '10px 15px',
                                        cursor: 'pointer',
                                        borderBottom: '1px solid #f0f0f0',
                                        backgroundColor: selectedIndex === index ? '#f5f5f5' : 'white',
                                        transition: 'background-color 0.2s',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        gap: '10px'
                                    }}
                                >
                                    <div style={{ flex: 1 }}>
                                        <div style={{
                                            fontWeight: '600',
                                            fontSize: '14px',
                                            color: '#333',
                                            marginBottom: '2px'
                                        }}>
                                            {suggestion.title}
                                        </div>
                                        {suggestion.subtitle && (
                                            <div style={{
                                                fontSize: '12px',
                                                color: '#666',
                                                marginBottom: '2px'
                                            }}>
                                                {suggestion.subtitle}
                                            </div>
                                        )}
                                        {suggestion.address && (
                                            <div style={{
                                                fontSize: '11px',
                                                color: '#999',
                                            }}>
                                                {suggestion.address}
                                            </div>
                                        )}
                                    </div>
                                    {suggestion.imageUrl && (
                                        <div style={{
                                            width: '60px',
                                            height: '60px',
                                            flexShrink: 0,
                                            overflow: 'hidden',
                                            borderRadius: '4px',
                                            border: '1px solid #e0e0e0'
                                        }}>
                                            <img 
                                                src={suggestion.imageUrl} 
                                                alt={suggestion.title}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover'
                                                }}
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>,
                        document.body
                    )}
                </div>
            </div>
        </div>
    );
};

const DesignMap = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [geoJsonData, setGeoJsonData] = useState(null);
    const [MonthlyProject, setProject] = useState({});
    const [shareUrl, setShareUrl] = useState('');
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
    const [mapInitialized, setMapInitialized] = useState(false);

    // Meta filters state - initialize all to false
    const [metaFilters, setMetaFilters] = useState({
        pointsOfInterest: false,
        awards: false,
        pointsOfSales: false,
        designEvents: false
    });
    
    // Specific filters within categories
    const [selectedFilters, setSelectedFilters] = useState({
        pointsOfInterest: null,
        designEvent: null,
        pointsOfSales: null,
        awards: null,
        district: null
    });
    
    // Expandable sections in filter panel
    const [expandedSections, setExpandedSections] = useState({
        pointsOfInterest: false,
        designEvent: false,
        pointsOfSales: false,
        awards: false,
        district: false
    });
    
    const mapContainer = useRef(null);
    const map = useRef(null);
    const sidebarRef = useRef(null);
    const iconCache = useRef({});
    const [sidebarContent, setSidebarContent] = useState(null);
    const [isSidebarVisible, setIsSidebarVisible] = useState(false);

    // Check if we're on a mobile device
    const isMobile = windowWidth <= 768;

    // Listen for window resize
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Toggle filters panel on mobile
    const toggleFilters = () => {
        setIsFilterOpen(!isFilterOpen);
    };

    // Function to update the share URL based on current map state
    const updateShareUrl = () => {
            if (!map.current || !mapInitialized) return;
        
        // Get current filter state
        const filterType = Object.keys(selectedFilters).find(key => selectedFilters[key] !== null);
        const filterValue = filterType ? selectedFilters[filterType] : null;
        
        // Get current map view
        const center = map.current.getCenter();
        const mapView = {
            lat: center.lat,
            lng: center.lng,
            zoom: map.current.getZoom()
        };
        
        // Generate and set the share URL
        const newShareUrl = generateShareLink(filterType, filterValue, mapView);
        setShareUrl(newShareUrl);
    };

    // Toggle section in filter panel
    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    // Helper function to get the correct meta filter key from a filter type
    const getMetaFilterKey = (filterType) => {
        switch(filterType) {
            case 'pointsOfInterest': return 'pointsOfInterest';
            case 'awards': return 'awards';
            case 'pointsOfSales': return 'pointsOfSales';
            case 'designEvent': return 'designEvents';
            case 'district': return 'districts';
            default: return filterType;
        }
    };

    // Toggle meta filter to show/hide categories
    const toggleMetaFilter = (filterType) => {
        // Get the correct meta key
        const metaKey = getMetaFilterKey(filterType);
        
        // Create a new meta filters object
        const newMetaFilters = { 
            pointsOfInterest: false,
            awards: false,
            pointsOfSales: false,
            designEvents: false
        };
        
        // Toggle the selected meta filter (only one can be active at a time)
        newMetaFilters[metaKey] = !metaFilters[metaKey];
        
        // Clear specific filters
        setSelectedFilters({
            pointsOfInterest: null,
            designEvent: null,
            pointsOfSales: null,
            awards: null,
            district: null
        });
        
        // Update meta filters state
        setMetaFilters(newMetaFilters);
        
        // Apply filters based on updated meta filters
        if (map.current && geoJsonData) {
            if (newMetaFilters[metaKey]) {
                // If we're turning on a meta filter, apply it
                applyMetaFilter(metaKey, true);
            } else {
                // If we're turning off all filters, reset the map
                resetMapFilters();
            }
        }
        
        // Update the share URL after filters change
        setTimeout(updateShareUrl, 100);
    };

    // Reset map to show all features
    const resetMapFilters = () => {
        if (map.current && geoJsonData) {
            map.current.setFilter('vectorLayer', ['!', ['has', 'point_count']]);
            map.current.setFilter('clusters', ['has', 'point_count']);
            if (map.current.getSource('vectorSource')) {
                map.current.getSource('vectorSource').setData(geoJsonData);
            }
            
            // Update the share URL after resetting filters
            setTimeout(updateShareUrl, 100);
        }
    };

    // Apply meta filter
    const applyMetaFilter = (metaCategory, isActive) => {
        if (!map.current || !geoJsonData) return;
        
        if (!isActive) {
            // If deactivating, reset to show all features
            resetMapFilters();
            return;
        }
        
        // Filter features based on the meta category
        let filteredFeatures = [];
        switch (metaCategory) {
            case 'pointsOfInterest':
                // Get all features that have any points_of_interest value
                filteredFeatures = geoJsonData.features.filter(feature => 
                    feature.properties.points_of_interest && 
                    feature.properties.points_of_interest.length > 0
                );
                break;
            case 'awards':
                // Get all features that have interior_designers values matching award years
                filteredFeatures = geoJsonData.features.filter(feature =>
                    feature.properties.interior_designers &&
                    ['2019', '2022', '2024', '2025'].includes(feature.properties.interior_designers)
                );
                break;
            case 'pointsOfSales':
                // Get all features that are points of sale
                filteredFeatures = geoJsonData.features.filter(feature =>
                    feature.properties.projectType === 'pos' ||
                    (feature.properties.design_points_of_sales && 
                    feature.properties.design_points_of_sales.length > 0)
                );
                break;
            case 'designEvents':
                // Get all features related to design events (ITC 2026, Oui Design, and Paris Design Week)
                filteredFeatures = geoJsonData.features.filter(feature =>
                    feature.properties.projectType === 'itc26' ||
                    feature.properties.projectType === 'oui' ||
                    feature.properties.projectType === 'pdw'
                );
                break;
            case 'districts':
                // Get all features that have district stored in database only
                filteredFeatures = geoJsonData.features.filter(feature => {
                    // Only check if district is stored in database
                    return feature.properties.district && feature.properties.district.length > 0;
                });
                break;
            default:
                filteredFeatures = geoJsonData.features;
        }
        
        // Update the source data with filtered features
        const filteredGeoJson = {
            type: 'FeatureCollection',
            features: filteredFeatures
        };
        
        if (map.current.getSource('vectorSource')) {
            map.current.getSource('vectorSource').setData(filteredGeoJson);
        }
        
        // Update the share URL after filters change
        setTimeout(updateShareUrl, 100);
    };
    
    // Component for the meta filter checkbox that appears in section headers
    const SectionHeaderCheckbox = ({ filterType, isActive, onClick }) => {
        return (
            <div 
                onClick={(e) => {
                    e.stopPropagation(); // Prevent triggering section toggle
                    onClick(filterType);
                }}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginLeft: '10px',
                    marginRight: '10px',
                    cursor: 'pointer'
                }}
            >
                <div style={{
                    width: '16px',
                    height: '16px',
                    border: '1px solid #40549e',
                    borderRadius: '3px',
                    backgroundColor: isActive ? '#40549e' : 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '5px'
                }}>
                    {isActive && (
                        <span style={{ color: 'white', fontSize: '12px' }}>✓</span>
                    )}
                </div>
                <span style={{ fontSize: '10px', color: isActive ? '#40549e' : '#666' }}>
                    Show all
                </span>
            </div>
        );
    };

    // Generate spiral offsets for overlapping points
    const generateSpiralOffsets = (features) => {
        // Safety check - make sure we have valid features
        if (!features || !Array.isArray(features)) {
            console.error("Invalid features array provided to generateSpiralOffsets");
            return features || [];
        }
        
        // Group features by their coordinates
        const coordGroups = {};
        
        features.forEach(feature => {
            // Skip invalid features
            if (!feature || !feature.geometry || !feature.geometry.coordinates) {
                console.warn("Skipping invalid feature:", feature);
                return;
            }
            
            // Format coordinates safely, handling different possible formats
            let coordKey;
            try {
                const coords = feature.geometry.coordinates;
                // Check if coordinates are an array with at least 2 elements
                if (Array.isArray(coords) && coords.length >= 2) {
                    const lon = typeof coords[0] === 'number' ? coords[0] : parseFloat(coords[0]);
                    const lat = typeof coords[1] === 'number' ? coords[1] : parseFloat(coords[1]);
                    
                    // Skip if we couldn't parse the coordinates as numbers
                    if (isNaN(lon) || isNaN(lat)) {
                        console.warn("Invalid coordinates in feature:", coords);
                        return;
                    }
                    
                    // Create a consistent string key for grouping
                    coordKey = `${lon.toFixed(6)},${lat.toFixed(6)}`;
                } else {
                    console.warn("Coordinates not in expected format:", coords);
                    return;
                }
            } catch (error) {
                console.error("Error processing coordinates:", error, feature);
                return;
            }
            
            // Add to coordinate group
            if (!coordGroups[coordKey]) {
                coordGroups[coordKey] = [];
            }
            coordGroups[coordKey].push(feature);
        });
        
        // Process all coordinates groups
        Object.entries(coordGroups).forEach(([coordKey, group]) => {
            // Only apply offsets if there's more than one point at these coordinates
            if (group.length > 1) {
                // Improved spiral parameters
                const spiralScale = 0.0002; // Scale factor for the spiral
                const angleStep = (2 * Math.PI) / group.length;
                
                // Apply spiral offset to each point in the group
                group.forEach((feature, index) => {
                    try {
                        // Apply offset to all points except the first one
                        if (index > 0) {
                            const angle = index * angleStep;
                            const radius = spiralScale * index;
                            
                            // Calculate offset using spiral formula
                            const dx = radius * Math.cos(angle);
                            const dy = radius * Math.sin(angle);
                            
                            // Get original coordinates as numbers
                            const coords = feature.geometry.coordinates;
                            const lon = typeof coords[0] === 'number' ? coords[0] : parseFloat(coords[0]);
                            const lat = typeof coords[1] === 'number' ? coords[1] : parseFloat(coords[1]);
                            
                            // Apply offset to coordinates
                            feature.geometry.coordinates = [lon + dx, lat + dy];
                        }
                    } catch (error) {
                        console.error("Error offsetting feature:", error, feature);
                    }
                });
            }
        });
        
        // Return all features with updated coordinates
        return Object.values(coordGroups).flat();
    };
    
    // Store map reference globally for search dropdown
    useEffect(() => {
        window.map = map;
        return () => {
            delete window.map;
        };
    }, []);

    // Preload icons before map initialization
    const preloadIcons = async (mapInstance) => {
        try {
            const allIcons = { ...POINT_ICONS, ...AWARDS_ICONS, ...EVENT_TYPE_ICONS };
            
            // Additional icon mappings for map layer compatibility
            const iconMappings = {
                'retail-icon': POINT_ICONS['Retail'],
                'hotel-icon': POINT_ICONS['Hotel'],
                'office-icon': POINT_ICONS['Office'],
                'restaurant-bar-icon': POINT_ICONS['Restaurant/bar'],
                'cultural-space-icon': POINT_ICONS['Cultural space'],
                'private-residence-icon': POINT_ICONS['Private residence'],
                'urban-space-transportation-icon': POINT_ICONS['Urban space/Transportation'],
                'architecture-icon': POINT_ICONS['Architecture'],
                'installation-scenography-icon': POINT_ICONS['Installation/Scenography'],
                'city-icon': city,
                'oui-design-icon': ouiDesignIcon,
                'points-of-sale-icon': retailIcon
            };
            
            // Combine all icons
            const combinedIcons = { ...allIcons, ...iconMappings };
            
            await Promise.all(
                Object.entries(combinedIcons).map(([key, path]) => {
                    return new Promise((resolve, reject) => {
                        if (!mapInstance.hasImage(key)) {
                            const image = new Image();
                            image.src = path;
                            
                            
                            image.onload = () => {
                                const canvas = document.createElement('canvas');
                                canvas.width = image.width;
                                canvas.height = image.height;
                                const ctx = canvas.getContext('2d');
                                ctx.drawImage(image, 0, 0);
                                mapInstance.addImage(key, ctx.getImageData(0, 0, canvas.width, canvas.height), { sdf: false });
                                
                                
                                resolve();
                            };
                            image.onerror = (err) => {
                                console.error(`Failed to load image for key "${key}" from path "${path}"`, err);
                                reject(err);
                            };
                        } else {
                            resolve();
                        }
                    });
                })
            );
            
        } catch (error) {
            console.error("Error preloading icons:", error);
        }
    };
    
    useEffect(() => {
        if (map.current) {
            preloadIcons(map.current);
        }

        api.GetMonthlyMapProject()
            .then((response) => {
                setProject(response.data);
            })
            .catch((error) => {
                console.log(error);
            });
    }, []);

    useEffect(() => {
        const fetchSupabaseData = async () => {
            try {
                const { data: projectsData, error: projectsError } = await supabase
                    .from('projects')
                    .select('*');
                    
                if (projectsError) throw projectsError;

                // Fetch ITC 2025 projects
                const { data: itcData, error: itcError } = await supabase
                    .from('itc_2025')
                    .select('*');

                if (itcError) throw itcError;

                const { data: posData, error: posError } = await supabase
                    .from('points_of_sale')
                    .select('*');
            
                const regularFeatures = projectsData.filter(project => project.points_of_interest).map(project => ({
                    type: "Feature",
                    properties: {
                        nom_etablissement: project.nom_etablissement,
                        fiche_agence: project.fiche_agence,
                        titre_affiche: project.titre_affiche,
                        adresse: project.adresse,
                        interior_designers: project.interior_designers,
                        points_of_interest: project.points_of_interest,
                        design_events: project.design_events,
                        design_points_of_sales: project.design_points_of_sales,
                        annee_projet: project.annee_projet,
                        description_projet: project.description_projet,
                        date_vernissage: project.date_vernissage,
                        heure_vernissage: project.heure_vernissage,
                        date_debut_expo: project.date_debut_expo,
                        date_fin_expo: project.date_fin_expo,
                        site_web: project.site_web,
                        credits_photo: project.credits_photo,
                        photos_projet: project.photos_projet,
                        description_agence_fr: project.description_agence_fr,
                        email: project.email,
                        instagram: project.instagram,
                        projectType: 'regular',
                        mongo_company_id: project.mongo_company_id,
                        mongo_project_id: project.mongo_project_id,
                    },
                    geometry: {
                        type: "Point",
                        coordinates: [project.longitude, project.latitude]
                    }
                }));

                // log all installation/scenography projects
                const installationScenographyFeatures = projectsData
                    .filter(project => project.points_of_interest && project.points_of_interest.includes('Installation/Scenography'))
                
                console.log("Installation/Scenography Projects:", installationScenographyFeatures);

                
                // Separate Oui Design projects from ITC 
                const ouiDesignFeatures = itcData
                    .filter(project => project.design_events === 'Oui Design! 2025')
                    .map(project => ({
                        type: "Feature",
                        properties: {
                            nom_etablissement: project.nom_etablissement,
                            adresse: project.adresse,
                            description_projet: project.description_projet,
                            instagram: project.instagram,
                            site_web: project.site_web,
                            design_events: project.design_events,
                            event_type: project.event_type || 'Exhibition',
                            date_debut_expo: project.date_debut_expo,
                            date_fin_expo: project.date_fin_expo,
                            vernissage_date: project.vernissage_date,
                            vernissage_hour: project.vernissage_hour,
                            contact_rsvp: project.contact_rsvp,
                            rsvp: project.rsvp,
                            photos_projet: "",
                            projectType: 'oui',
                            district: project.district || null
                        },
                        geometry: {
                            type: "Point",
                            coordinates: [project.longitude, project.latitude]
                        }
                    }));
                    
                // Separate Paris Design Week projects
                const parisDesignWeekFeatures = itcData
                    .filter(project => project.design_events === 'Paris Design Week 2025')
                    .map(project => ({
                        type: "Feature",
                        properties: {
                            nom_etablissement: project.nom_etablissement,
                            adresse: project.adresse,
                            description_projet: project.description_projet,
                            instagram: project.instagram,
                            site_web: project.site_web,
                            design_events: project.design_events,
                            event_type: project.event_type || 'Exhibition',
                            date_debut_expo: project.date_debut_expo,
                            date_fin_expo: project.date_fin_expo,
                            vernissage_date: project.vernissage_date,
                            vernissage_hour: project.vernissage_hour,
                            contact_rsvp: project.contact_rsvp,
                            rsvp: project.rsvp,
                            photos_projet: "",
                            projectType: 'pdw',
                            district: project.district || null
                        },
                        geometry: {
                            type: "Point",
                            coordinates: [project.longitude, project.latitude]
                        }
                    }));
        
                // Process In The City 2026 projects
                const itc2026Features = itcData
                    .filter(project => project.design_events === 'In The City 2026')
                    .map(project => ({
                        type: "Feature",
                        properties: {
                            nom_etablissement: project.nom_etablissement,
                            adresse: project.adresse,
                            description_projet: project.description_projet,
                            instagram: project.instagram,
                            site_web: project.site_web,
                            design_events: project.design_events,
                            event_type: project.event_type || 'Exhibition',
                            date_debut_expo: project.date_debut_expo,
                            date_fin_expo: project.date_fin_expo,
                            vernissage_date: project.vernissage_date,
                            vernissage_hour: project.vernissage_hour,
                            contact_rsvp: project.contact_rsvp,
                            rsvp: project.rsvp,
                            photos_projet: "",
                            projectType: 'itc26',
                            district: project.district || null
                        },
                        geometry: {
                            type: "Point",
                            coordinates: [project.longitude, project.latitude]
                        }
                    }));

                const posFeatures = posData.map(pos => ({
                    type: "Feature",
                    properties: {
                        nom_etablissement: pos.fiche_agence,
                        titre_affiche: pos.titre_affiche,
                        adresse: pos.adresse,
                        design_points_of_sales: pos.design_points_of_sales,
                        photos_projet: pos.photos_projet || '',
                        projectType: 'pos',
                        description_projet: pos.description || '',
                        logo: pos.logo || '',
                        website: pos.website || '',
                        phone: pos.phone || '',
                        pos_email: pos.email || '',
                        opening_hours: pos.opening_hours || '',
                    },
                    geometry: {
                        type: "Point",
                        coordinates: [pos.longitude, pos.latitude]
                    }
                }));

                // Combine all types of features (excluding old ITC 2025 - itcFeatures)
                let allFeatures = [...regularFeatures, ...itc2026Features, ...ouiDesignFeatures, ...parisDesignWeekFeatures, ...posFeatures];

                allFeatures = generateSpiralOffsets(allFeatures);
        
                // Create GeoJSON collection
                const geoJsonData = {
                    type: "FeatureCollection",
                    features: allFeatures
                };
        
                setGeoJsonData(geoJsonData);
    
                mapboxgl.accessToken = 'pk.eyJ1IjoiZGdjb2Jvc3MiLCJhIjoiY2xzY2JnajIzMGZsNzJpcGM4b3l5OWttaCJ9.1GzkF8EERgQ5u1jkmP3C7w';
                
                map.current = new mapboxgl.Map({
                    container: mapContainer.current,
                    style: 'mapbox://styles/dgcoboss/cm5k9ztpe003g01s76e7v8owz',
                    center: [2.349014, 0],
                    zoom: 2,
                    projection: 'mercator',
                });
    
                map.current.on('load', () => {
                    // Add logo
                    const logo = document.createElement('img');
                    logo.src = 'logomap.svg';
                    logo.alt = 'Map Logo';
                    logo.id = 'logo-overlay';
                    logo.style.position = 'absolute';
                    logo.style.top = '10px';
                    logo.style.left = '10px';
                    logo.style.width = '70px';
                    logo.style.height = '70px';
                    logo.style.zIndex = '1';
                    logo.style.cursor = 'pointer';
                    mapContainer.current.appendChild(logo);
    
                    // Add source with clustering enabled
                    map.current.addSource('vectorSource', {
                        type: 'geojson',
                        data: geoJsonData,
                        cluster: true,
                        clusterMaxZoom: 14,
                        clusterRadius: 50
                    });

                    // Load cluster icon
                    map.current.loadImage(clusterIcon, (error, image) => {
                        if (error) throw error;
                        map.current.addImage('cluster-icon', image);
                    });

                    map.current.loadImage(retailIcon, (error, image) => {
                        if (error) throw error;
                        if (!map.current.hasImage('points-of-sale-icon')) {
                            map.current.addImage('points-of-sale-icon', image);
                        }
                    });
                    
                    // Load the Oui Design icon
                    map.current.loadImage(ouiDesignIcon, (error, image) => {
                        if (error) throw error;
                        if (!map.current.hasImage('oui-design-icon')) {
                            map.current.addImage('oui-design-icon', image);
                        }
                    });
                    
                    // Load the Paris Design Week icon
                    map.current.loadImage(parisDesignWeekIcon, (error, image) => {
                        if (error) throw error;
                        if (!map.current.hasImage('paris-design-week-icon')) {
                            map.current.addImage('paris-design-week-icon', image);
                        }
                    });

                    // Use preloaded icons for map
                    const loadImagePromises = Object.entries(POINT_ICONS).map(([category, iconPath]) => {
                        return new Promise((resolve, reject) => {
                            const iconId = `${category.toLowerCase().replace(/[^a-z0-9]/g, '-')}-icon`;
                            
                            
                            if (map.current.hasImage(iconId)) {
                                resolve();
                                return;
                            }

                            const cachedIcon = iconCache.current[iconPath];
                            if (cachedIcon) {
                                const canvas = document.createElement('canvas');
                                const ctx = canvas.getContext('2d');
                                canvas.width = cachedIcon.width;
                                canvas.height = cachedIcon.height;
                                ctx.imageSmoothingQuality = 'high';
                                ctx.drawImage(cachedIcon, 0, 0);
                                
                                if (!map.current.hasImage(iconId)) {
                                    map.current.addImage(iconId, ctx.getImageData(0, 0, canvas.width, canvas.height));
                                }
                                resolve();
                            } else {
                                map.current.loadImage(iconPath, (error, image) => {
                                    if (error) {
                                        console.error(`Error loading icon for ${category}:`, error);
                                        reject(error);
                                        return;
                                    }
                                    if (!map.current.hasImage(iconId)) {
                                        map.current.addImage(iconId, image);
                                    }
                                    resolve();
                                });
                            }
                        });
                    });
                    
                    // Load award icons
                    const loadAwardIcons = async () => {
                        await Promise.all(Object.entries(AWARDS_ICONS).map(([key, iconPath]) => 
                            new Promise((resolve, reject) => {
                                if (!map.current.hasImage(key)) {
                                    map.current.loadImage(iconPath, (error, image) => {
                                        if (error) {
                                            console.error(`Error loading ${key}:`, error);
                                            reject(error);
                                            return;
                                        }
                                        map.current.addImage(key, image);
                                        resolve();
                                    });
                                } else {
                                    resolve();
                                }
                            })
                        ));
                    };
                    
                    Promise.all(loadImagePromises)
                        .then(() => {
                            // Add cluster layer
                            map.current.addLayer({
                                id: 'clusters',
                                type: 'symbol',
                                source: 'vectorSource',
                                filter: ['has', 'point_count'],
                                layout: {
                                    'icon-image': 'cluster-icon',
                                    'icon-size': 0.5,
                                    'icon-allow-overlap': true,
                                    'text-field': '{point_count_abbreviated}',
                                    'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
                                    'text-size': 20,
                                    'text-offset': [1.5, -1.5],
                                    'text-anchor': 'top-right',
                                },
                                paint: {
                                    'text-color': 'black',
                                    'text-halo-color': 'white',
                                    'text-halo-width': 2,
                                    'text-halo-blur': 1,
                                }
                            });

                            map.current.loadImage(city, (error, image) => {
                                if (error) throw error;
                                if (!map.current.hasImage('city-icon')) {
                                    map.current.addImage('city-icon', image);
                                }
                            });

                            // Load the In The City 2026 icon
                            map.current.loadImage(city2026, (error, image) => {
                                if (error) throw error;
                                if (!map.current.hasImage('city-2026-icon')) {
                                    map.current.addImage('city-2026-icon', image);
                                }
                            });

                            // Return the loadAwardIcons promise
                            return loadAwardIcons();
                        })
                        .then(() => {
                            // Add vector layer after award icons are loaded
                            map.current.addLayer({
                                id: 'vectorLayer',
                                type: 'symbol',
                                source: 'vectorSource',
                                filter: ['!', ['has', 'point_count']],
                                layout: {
                                    'icon-image': [
                                        'case',
                                        ['==', ['get', 'projectType'], 'itc'],
                                        'city-icon',
                                        ['==', ['get', 'projectType'], 'itc26'],
                                        'city-2026-icon',
                                        ['==', ['get', 'projectType'], 'oui'],
                                        'oui-design-icon',
                                        ['==', ['get', 'projectType'], 'pdw'],
                                        'paris-design-week-icon',
                                        ['==', ['get', 'projectType'], 'pos'],
                                        'points-of-sale-icon',
                                        [
                                            'let',
                                            'poi',
                                            ['get', 'points_of_interest'],
                                            [
                                                'case',
                                                ['all',
                                                    ['==', ['get', 'interior_designers'], '2025'],
                                                    ['!=', ['var', 'poi'], '']
                                                ],
                                                ['concat', ['var', 'poi'], '-2025'],
                                                ['all',
                                                    ['==', ['get', 'interior_designers'], '2024'],
                                                    ['!=', ['var', 'poi'], '']
                                                ],
                                                ['concat', ['var', 'poi'], '-2024'],
                                                ['all',
                                                    ['==', ['get', 'interior_designers'], '2022'],
                                                    ['!=', ['var', 'poi'], '']
                                                ],
                                                ['concat', ['var', 'poi'], '-2022'],
                                                ['all',
                                                    ['==', ['get', 'interior_designers'], '2019'],
                                                    ['!=', ['var', 'poi'], '']
                                                ],
                                                ['concat', ['var', 'poi'], '-2019'],
                                                [
                                                    'match',
                                                    ['var', 'poi'],
                                                    'Retail', 'retail-icon',
                                                    'Hotel', 'hotel-icon',
                                                    'Office', 'office-icon',
                                                    'Restaurant/bar', 'restaurant-bar-icon',
                                                    'Cultural space', 'cultural-space-icon',
                                                    'Private residence', 'private-residence-icon',
                                                    'Urban space/Transportation', 'urban-space-transportation-icon',
                                                    'Architecture', 'architecture-icon',
                                                    'Installation/Scenography', 'installation-scenography-icon',
                                                    'default-icon'
                                                ]
                                            ]
                                        ]
                                    ],
                                    'icon-size': 0.4,
                                    'icon-allow-overlap': true
                                }
                            });
                            
                            // Apply filter from URL parameters if present
                            const { filterType, filterValue, mapView } = handleUrlParams();

                            if (filterType && filterValue) {
                                // Set the selected filter in our state
                                setSelectedFilters(prev => ({
                                    ...prev,
                                    [filterType]: filterValue
                                }));
                                
                                // For specific design event filters
                                if (filterType === 'designEvent' && filterValue === 'Oui Design! 2025') {
                                    // Filter to show only Oui Design projects
                                    const ouiDesignFeatures = geoJsonData.features.filter(feature => 
                                        feature.properties.projectType === 'oui'
                                    );
                                    
                                    const filteredGeoJson = {
                                        type: 'FeatureCollection',
                                        features: ouiDesignFeatures
                                    };
                                    
                                    if (map.current.getSource('vectorSource')) {
                                        map.current.getSource('vectorSource').setData(filteredGeoJson);
                                    }
                                    
                                    // Apply the filter to the map layers
                                    map.current.setFilter('vectorLayer', ['all', 
                                        ['!', ['has', 'point_count']], 
                                        ['==', ['get', 'projectType'], 'oui']
                                    ]);
                                } else if (filterType === 'designEvent' && filterValue === 'Paris Design Week 2025') {
                                    // Filter to show only Paris Design Week projects
                                    const parisDesignWeekFeatures = geoJsonData.features.filter(feature =>
                                        feature.properties.projectType === 'pdw'
                                    );

                                    const filteredGeoJson = {
                                        type: 'FeatureCollection',
                                        features: parisDesignWeekFeatures
                                    };

                                    if (map.current.getSource('vectorSource')) {
                                        map.current.getSource('vectorSource').setData(filteredGeoJson);
                                    }

                                    // Apply the filter to the map layers
                                    map.current.setFilter('vectorLayer', ['all',
                                        ['!', ['has', 'point_count']],
                                        ['==', ['get', 'projectType'], 'pdw']
                                    ]);
                                } else if (filterType === 'designEvent' && filterValue === 'In The City 2026') {
                                    // Filter to show only In The City 2026 projects
                                    const itc2026Features = geoJsonData.features.filter(feature =>
                                        feature.properties.projectType === 'itc26'
                                    );

                                    const filteredGeoJson = {
                                        type: 'FeatureCollection',
                                        features: itc2026Features
                                    };

                                    if (map.current.getSource('vectorSource')) {
                                        map.current.getSource('vectorSource').setData(filteredGeoJson);
                                    }

                                    // Apply the filter to the map layers
                                    map.current.setFilter('vectorLayer', ['all',
                                        ['!', ['has', 'point_count']],
                                        ['==', ['get', 'projectType'], 'itc26']
                                    ]);
                                } else {
                                    // Apply the standard filter
                                    applyFilters(filterType, filterValue);
                                }
                                
                                // Expand the appropriate section in the filter panel
                                setExpandedSections(prev => ({
                                    ...prev,
                                    [filterType]: true
                                }));
                            }

                            // Set the map view if specified in URL
                            if (mapView.lat && mapView.lng) {
                                map.current.flyTo({
                                    center: [mapView.lng, mapView.lat],
                                    zoom: mapView.zoom || 12,
                                    essential: true,
                                    duration: 1000
                                });
                            }
                            
                            // Generate current share URL
                            updateShareUrl();
                            
                            // Update share URL when the map moves
                            map.current.on('moveend', updateShareUrl);
                            
                            // Generate and log the Oui Design share link
                            const ouiDesignLink = createOuiDesignShareLink();
                        })
                        .catch(error => {
                            console.error('Error loading icons:', error);
                            map.current.addLayer({
                                id: 'vectorLayer',
                                type: 'circle',
                                source: 'vectorSource',
                                paint: {
                                    'circle-radius': 4,
                                    'circle-color': '#1534da',
                                    'circle-stroke-color': 'black',
                                    'circle-stroke-width': 1,
                                    'circle-opacity': 0.8
                                }
                            });
                        });

                    // Add cluster click handler
                    map.current.on('click', 'clusters', (e) => {
                        const features = map.current.queryRenderedFeatures(e.point, {
                            layers: ['clusters']
                        });
                        const clusterId = features[0].properties.cluster_id;
                        const source = map.current.getSource('vectorSource');
                        if (source && source.getClusterExpansionZoom) {
                            source.getClusterExpansionZoom(
                                clusterId,
                                (error, zoom) => {
                                    if (error) return;
                                    
                                    map.current.easeTo({
                                        center: features[0].geometry.coordinates,
                                        zoom: zoom + 1
                                    });
                                }
                            );
                        }
                    });

                    // Add hover effects for clusters
                    map.current.on('mouseenter', 'clusters', () => {
                        map.current.getCanvas().style.cursor = 'pointer';
                    });

                    map.current.on('mouseleave', 'clusters', () => {
                        map.current.getCanvas().style.cursor = '';
                    });

                    const popup = new mapboxgl.Popup({
                        closeButton: false,
                        closeOnClick: false,
                        offset: 15,
                        maxWidth: '300px'
                    });
                    
                    // Add hover effects for individual points
                    map.current.on('mouseenter', 'vectorLayer', (e) => {
                        map.current.getCanvas().style.cursor = 'pointer';
                        
                        const coordinates = e.features[0].geometry.coordinates.slice();
                        const properties = e.features[0].properties;
                        
                        // Create popup content based on project type
                        let popupContent;
                        
                        if (properties.projectType === 'itc') {
                            popupContent = `
                                <div class="mappopup" style="width: 200px; padding:0; border:1px solid; margin:none;">
                                    <img src="${city_card}" alt="In The City" style="width: 80%; height: auto;display:block; margin-bottom: 8px;margin:auto">
                                    <div class="infocontainer" style="padding: 8px;">
                                        <div style="margin-bottom: 4px; color:blue; font-weight: bold;">
                                            ${properties.nom_etablissement}
                                        </div>
                                        <div style="font-size: 12px; text-transform:none; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;">
                                            ${properties.description_projet || ''}
                                        </div>
                                    </div>
                                </div>
                            `;
                        } else if (properties.projectType === 'itc26') {
                            popupContent = `
                                <div class="mappopup" style="width: 200px; padding:0; border:1px solid; margin:none;">
                                    <img src="${city2026_card}" alt="In The City 2026" style="width: 80%; height: auto;display:block; margin-bottom: 8px;margin:auto">
                                    <div class="infocontainer" style="padding: 8px;">
                                        <div style="margin-bottom: 4px; color:blue; font-weight: bold;">
                                            ${properties.nom_etablissement}
                                        </div>
                                        <div style="font-size: 12px; text-transform:none; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;">
                                            ${properties.description_projet || ''}
                                        </div>
                                    </div>
                                </div>
                            `;
                        } else if (properties.projectType === 'oui') {
                            popupContent = `
                                <div class="mappopup" style="width: 200px; padding:0; border:1px solid; margin:none;">
                                    <img src="${ouiDesign_card}" alt="Oui Design 2025" style="width: 80%; height: auto;display:block; margin-bottom: 8px;margin:auto">
                                    <div class="infocontainer" style="padding: 8px;">
                                        <div style="margin-bottom: 4px; color:blue; font-weight: bold;">
                                            ${properties.nom_etablissement}
                                        </div>
                                        <div style="font-size: 12px; color: #e91e63; margin-bottom: 4px;">
                                            ${properties.event_type || 'Exhibition'}
                                        </div>
                                        <div style="font-size: 12px; text-transform:none; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;">
                                            ${properties.description_projet || ''}
                                        </div>
                                    </div>
                                </div>
                            `;
                        } else if (properties.projectType === 'pdw') {
                            popupContent = `
                                <div class="mappopup" style="width: 200px; padding:0; border:1px solid; margin:none;">
                                    <img src="${parisDesignWeek_card}" alt="Paris Design Week 2025" style="width: 80%; height: auto;display:block; margin-bottom: 8px;margin:auto">
                                    <div class="infocontainer" style="padding: 8px;">
                                        <div style="margin-bottom: 4px; color:blue; font-weight: bold;">
                                            ${properties.nom_etablissement}
                                        </div>
                                        <div style="font-size: 12px; color: #e91e63; margin-bottom: 4px;">
                                            ${properties.event_type || 'Exhibition'}
                                        </div>
                                        <div style="font-size: 12px; text-transform:none; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;">
                                            ${properties.description_projet || ''}
                                        </div>
                                    </div>
                                </div>
                            `;
                        } else if (properties.projectType === 'pos') {
                            const mediaBase = process.env.REACT_APP_MEDIA_BASE_URL || '';
                            const posImgSrc = properties.logo
                                ? mediaBase + 'userfiles/' + properties.logo
                                : (properties.photos_projet ? properties.photos_projet.split(',')[0].trim() : retailIcon);
                            popupContent = `
                                <div class="mappopup" style="width: 200px;padding:0; border:1px solid;margin:none;">
                                    <img style="max-width:80%; margin:auto;display:block; margin-bottom: 8px;" src="${posImgSrc}" alt="Point of Sale" onerror="this.style.display='none'">
                                    <div class="infocontainer" style="padding: 8px;">
                                        <div style="font-weight: bold; margin-bottom: 4px;color: red">Point of sale</div>
                                        <div style="margin-bottom: 4px;color:blue;font-weight: bold;">${properties.design_points_of_sales || ''}</div>
                                        <div style="font-weight: bold;">${properties.titre_affiche || ''}</div>
                                        ${properties.description_projet ? '<div style="font-size: 11px; margin-top: 4px; color: #666; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;">' + properties.description_projet + '</div>' : ''}
                                    </div>
                                </div>
                            `;
                        } else {
                            popupContent = `
                                <div class="mappopup" style="width: 200px;padding:0; border:1px solid;margin:none;">
                                    <img src="${properties.photos_projet ? properties.photos_projet.split(',')[0].trim() : ''}" alt="Project" style="width: 100%; height: 150px; object-fit: cover; margin-bottom: 8px;">
                                    <div class="infocontainer" style="padding: 8px;">
                                        <div style="font-weight: bold; margin-bottom: 4px;color: red">${formatCategoryForDisplay(properties.points_of_interest)}</div>
                                        <div style="margin-bottom: 4px;color:blue;font-weight: bold;">${properties.fiche_agence}</div>
                                        <div style="font-weight: bold;">${properties.titre_affiche}</div>
                                    </div>
                                </div>
                            `;
                        }

                        // Ensure that if the map is zoomed out such that multiple
                        // copies of the feature are visible, the popup appears
                        // over the copy being pointed to.
                        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
                        }

                        popup
                            .setLngLat(coordinates)
                            .setHTML(popupContent)
                            .addTo(map.current);
                    });

                    map.current.on('mouseleave', 'vectorLayer', () => {
                        map.current.getCanvas().style.cursor = '';
                        popup.remove();
                    });

                   map.current.on('click', 'vectorLayer', (e) => {
        if (e.features && e.features.length > 0) {
            const properties = e.features[0].properties;
            
            // Handle different project types
            let imageUrls = [];
            if (properties.projectType === 'pos') {
                // For points of sale, prefer logo, then photos_projet, then retail icon
                const posMediaBase = process.env.REACT_APP_MEDIA_BASE_URL || '';
                if (properties.logo) {
                    imageUrls = [posMediaBase + 'userfiles/' + properties.logo];
                } else if (properties.photos_projet) {
                    imageUrls = properties.photos_projet.split(',').map(url => url.trim());
                } else {
                    imageUrls = [retailIcon];
                }
            } else if (properties.projectType === 'itc') {
                imageUrls = [city_card];
            } else if (properties.projectType === 'itc26') {
                imageUrls = [city2026_card];
            } else if (properties.projectType === 'oui') {
                imageUrls = [ouiDesign_card];
            } else if (properties.projectType === 'pdw') {
                imageUrls = [parisDesignWeek_card];
            } else if (properties.photos_projet) {
                imageUrls = properties.photos_projet.split(',').map(url => url.trim());
            }

            // Store content for the sidebar
            setSidebarContent({
                images: imageUrls,
                properties: properties
            });
            
            // On mobile, show the mobile sidebar
            if (window.innerWidth <= 768) {
                setIsMobileSidebarOpen(true);
                // Ensure the desktop sidebar is hidden
                setIsSidebarVisible(false);
                
                // Remove any popup that might be showing
                const popups = document.getElementsByClassName('mapboxgl-popup');
                if (popups.length) {
                    Array.from(popups).forEach(popup => popup.remove());
                }
            } else {
                // On desktop, show the desktop sidebar
                setIsSidebarVisible(true);
                setIsMobileSidebarOpen(false);
            }
            
            // Update the share URL when a point is clicked
            setTimeout(updateShareUrl, 100);
        }
    });
    
    // Mark map as initialized
    setMapInitialized(true);
});
            } catch (error) {
                console.error('Error initializing map:', error);
            }
        };
    
        fetchSupabaseData();
    
        return () => {
            if (map.current) map.current.remove();
        };
    }, []); // Run once on component mount

    const handleSearch = (query) => {
        setSearchQuery(query);
        if (!map.current || !geoJsonData) return;
    
        if (!query) {
            // Reset all filters when search is cleared
            map.current.setFilter('vectorLayer', ['!', ['has', 'point_count']]);
            map.current.setFilter('clusters', ['has', 'point_count']);
            if (map.current.getSource('vectorSource')) {
                map.current.getSource('vectorSource').setData(geoJsonData);
            }
            
            // Reset zoom to initial world view
            map.current.flyTo({
                center: [2.349014, 0],  // Initial center coordinates
                zoom: 2,  // Initial zoom level
                essential: true,
                duration: 1000
            });
            
            // Update the share URL after search is cleared
            setTimeout(updateShareUrl, 100);
            return;
        }
    
        const lowercaseQuery = query.toLowerCase();
        
        // Score and filter features based on priority fields
        const scoredFeatures = geoJsonData.features
            .map(feature => {
                const properties = feature.properties;
                let score = 0;
                let matched = false;
                
                // Priority 1: nom_etablissement (highest priority)
                if (properties.nom_etablissement && 
                    properties.nom_etablissement.toString().toLowerCase().includes(lowercaseQuery)) {
                    score += 100;
                    matched = true;
                }
                
                // Priority 2: titre_affiche
                if (properties.titre_affiche && 
                    properties.titre_affiche.toString().toLowerCase().includes(lowercaseQuery)) {
                    score += 50;
                    matched = true;
                }
                
                // Priority 3: design_points_of_sales
                if (properties.design_points_of_sales && 
                    properties.design_points_of_sales.toString().toLowerCase().includes(lowercaseQuery)) {
                    score += 25;
                    matched = true;
                }
                
                // Priority 4: Other fields (lower priority)
                if (!matched) {
                    const otherFieldsMatch = Object.entries(properties).some(([key, val]) => {
                        if (key !== 'nom_etablissement' && key !== 'titre_affiche' && 
                            key !== 'design_points_of_sales' && val && 
                            val.toString().toLowerCase().includes(lowercaseQuery)) {
                            return true;
                        }
                        return false;
                    });
                    if (otherFieldsMatch) {
                        score += 1;
                        matched = true;
                    }
                }
                
                return { feature, score, matched };
            })
            .filter(item => item.matched)
            .sort((a, b) => b.score - a.score)
            .map(item => item.feature);
    
        // Update the source data with filtered features
        const filteredGeoJson = {
            type: 'FeatureCollection',
            features: scoredFeatures
        };
    
        // Update the source data to reflect the search results
        if (map.current.getSource('vectorSource')) {
            map.current.getSource('vectorSource').setData(filteredGeoJson);
        }
    
        // If exactly one result is found, fly to its location
        if (scoredFeatures.length === 1) {
            const coordinates = scoredFeatures[0].geometry.coordinates;
            map.current.flyTo({
                center: coordinates,
                zoom: 15,
                essential: true,
                duration: 2000 // Animation duration in milliseconds
            });
        }
        
        // Update the share URL after search
        setTimeout(updateShareUrl, 100);
    };

    const resetFilters = () => {
        setSelectedFilters({
            pointsOfInterest: null,
            designEvent: null,
            pointsOfSales: null,
            awards: null
        });
        
        setMetaFilters({
            pointsOfInterest: false,
            awards: false,
            pointsOfSales: false,
            designEvents: false,
            districts: false
        });
        
        resetMapFilters();
        
        // Close filter panel on mobile after resetting
        if (isMobile) {
            setIsFilterOpen(false);
        }
        
        // Update the share URL after resetting filters
        setTimeout(updateShareUrl, 100);
    };

    const applyFilters = (filterType, option) => {
        // Clear all meta filters when a specific filter is selected
        setMetaFilters({
            pointsOfInterest: false,
            awards: false,
            pointsOfSales: false,
            designEvents: false,
            districts: false
        });
        
        const newFilters = {
            ...selectedFilters,
            [filterType]: option
        };
        
        // Clear other filter types when one is selected
        if (filterType !== 'pointsOfInterest') newFilters.pointsOfInterest = null;
        if (filterType !== 'designEvent') newFilters.designEvent = null;
        if (filterType !== 'pointsOfSales') newFilters.pointsOfSales = null;
        if (filterType !== 'awards') newFilters.awards = null;
        if (filterType !== 'district') newFilters.district = null;
        
        setSelectedFilters(newFilters);
        
        if (!map.current || !geoJsonData) return;
        
        const filters = ['all'];
        
        if (newFilters.pointsOfInterest) {
            filters.push(['==', ['get', 'points_of_interest'], newFilters.pointsOfInterest]);
        }
        if (newFilters.designEvent) {
            // Special handling for design events
            if (newFilters.designEvent === 'Oui Design! 2025') {
                // Only show features where projectType is 'oui'
                filters.push(['==', ['get', 'projectType'], 'oui']);
            } else if (newFilters.designEvent === 'Paris Design Week 2025') {
                // Only show features where projectType is 'pdw'
                filters.push(['==', ['get', 'projectType'], 'pdw']);
            } else if (newFilters.designEvent === 'In The City 2026') {
                // Only show features where projectType is 'itc26'
                filters.push(['==', ['get', 'projectType'], 'itc26']);
            } else {
                // For In the City (legacy), use design_events property
                filters.push(['==', ['get', 'design_events'], newFilters.designEvent]);
            }
        }
        if (newFilters.pointsOfSales) {
            filters.push([
                'all',
                ['==', ['get', 'projectType'], 'pos'],
                ['==', ['get', 'design_points_of_sales'], newFilters.pointsOfSales]
            ]);
        }
        if (newFilters.awards) {
            // Convert award year string to interior_designers format
            const yearMap = {
                'Le FD100 - 1ère édition': '2019',
                'Le FD100 - 2ème édition': '2022',
                'Le FD100 - 3ème édition': '2024',
                'Le FD100 - 4ème édition': '2025'
            };
            const year = yearMap[newFilters.awards];
            if (year) {
                filters.push(['==', ['get', 'interior_designers'], year]);
            }
        }
        if (newFilters.district) {
            // District filter only applies to Paris Design Week events
            filters.push(['==', ['get', 'projectType'], 'pdw']);
        }
        
        if (filters.length > 1) {
            const iconFilter = ['all', ['!', ['has', 'point_count']], ...filters.slice(1)];
            map.current.setFilter('vectorLayer', iconFilter);
            map.current.setFilter('clusters', ['has', 'point_count']);
            
            // Filter the features based on the actual filters
            let allFeatures = geoJsonData.features.filter(feature => {
                return filters.slice(1).every(filter => {
                    if (filter[0] === 'all') {
                        return filter.slice(1).every(subFilter => {
                            const [, path, value] = subFilter;
                            return feature.properties[path[1]] === value;
                        });
                    }
                    const [, path, value] = filter;
                    return feature.properties[path[1]] === value;
                });
            });
            
            // Apply district filter if selected - already filtered to Paris Design Week events above
            if (newFilters.district) {
                allFeatures = allFeatures.filter(feature => 
                    propertyBelongsToDistrict(feature.properties, newFilters.district)
                );
            }
            
            const updatedGeoJson = {
                type: 'FeatureCollection',
                features: allFeatures
            };
            
            if (map.current.getSource('vectorSource')) {
                map.current.getSource('vectorSource').setData(updatedGeoJson);
            }
            
            // Center map based on design event type
            if (filterType === 'designEvent') {
                if (option === 'Oui Design! 2025') {
                    // Center on New York
                    map.current.flyTo({
                        center: [-73.9867, 40.7306],
                        zoom: 12,
                        essential: true,
                        duration: 1000
                    });
                } else if (option === 'Paris Design Week 2025') {
                    // Center on Paris
                    map.current.flyTo({
                        center: [2.3522, 48.8566],
                        zoom: 12,
                        essential: true,
                        duration: 1000
                    });
                } else if (option === 'In the City' || option === 'In The City 2026') {
                    // Center on Paris (In the City events are in Paris)
                    map.current.flyTo({
                        center: [2.3522, 48.8566],
                        zoom: 12,
                        essential: true,
                        duration: 1000
                    });
                }
            }
            
            // Center on Paris when district filter is applied (Paris Design Week events only)
            if (filterType === 'district') {
                map.current.flyTo({
                    center: [2.3522, 48.8566],
                    zoom: 12,
                    essential: true,
                    duration: 1000
                });
            }
        } else {
            resetMapFilters();
        }
        
        // Close filter panel on mobile after applying filter
        if (isMobile) {
            setIsFilterOpen(false);
        }
        
        // Update the share URL after applying filters
        setTimeout(updateShareUrl, 100);
    };

    // Render desktop sidebar content
    const renderDesktopSidebarContent = () => {
        if (!sidebarContent) return null;
    
        const { properties } = sidebarContent;
        const imageUrls = sidebarContent.images || [];
        
        return (
            <div 
                ref={sidebarRef}
                // className="sidebar-content" 
                className="sidebar-content slide-in-left" // Add this class
                style={{
                    position: 'absolute',
                    top: '10px',
                    left: '10px',
                    width: '300px',
                    maxHeight: 'calc(100vh - 100px)',
                    backgroundColor: 'white',
                    padding: '0',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                    zIndex: 2,
                    display: isSidebarVisible ? 'block' : 'none',
                    border: '1px solid black',
                    overflowY: 'auto'
                }}
            >
                <button 
                    onClick={() => setIsSidebarVisible(false)}
                    style={{
                        position: 'absolute',
                        top: '5px',
                        right: '5px',
                        background: 'none',
                        border: 'none',
                        fontSize: '30px',
                        cursor: 'pointer',
                        zIndex: 3,
                        padding: '5px',
                        width: '30px',
                        height: '30px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        filter: 'drop-shadow(0 0 4px black)',
                    }}
                >×</button>
                <div style={{ position: 'relative' }}>
                    <ImageSlider
                        images={imageUrls}
                        projectType={properties.projectType}
                        cityCardImage={properties.projectType === 'itc' ? city_card :
                                       properties.projectType === 'itc26' ? city2026_card :
                                       properties.projectType === 'oui' ? ouiDesign_card :
                                       properties.projectType === 'pdw' ? parisDesignWeek_card : null}
                    />
                </div>
                <PropertiesContainer properties={properties} />
            </div>
        );
    };

    // This is the critical part of your map component that handles showing property details
// Replace or update this section in your DesignMap component

// map.current.on('click', 'vectorLayer', (e) => {
//     if (e.features && e.features.length > 0) {
//         const properties = e.features[0].properties;
        
//         // Handle different project types
//         let imageUrls = [];
//         if (properties.projectType === 'pos') {
//             // For points of sale, use the photos_projet if available, otherwise use the retail icon
//             imageUrls = properties.photos_projet ? 
//                 properties.photos_projet.split(',').map(url => url.trim()) : 
//                 [retailIcon];
//         } else if (properties.projectType === 'itc') {
//             imageUrls = [city_card];
//         } else if (properties.projectType === 'oui') {
//             imageUrls = [ouiDesign_card];
//         } else if (properties.photos_projet) {
//             imageUrls = properties.photos_projet.split(',').map(url => url.trim());
//         }

//         // Store content for the sidebar
//         setSidebarContent({
//             images: imageUrls,
//             properties: properties
//         });
        
//         // On mobile, show the mobile sidebar
//         if (window.innerWidth <= 768) {
//             setIsMobileSidebarOpen(true);
//             // Ensure the desktop sidebar is hidden
//             setIsSidebarVisible(false);
            
//             // Remove any popup that might be showing
//             const popups = document.getElementsByClassName('mapboxgl-popup');
//             if (popups.length) {
//                 Array.from(popups).forEach(popup => popup.remove());
//             }
//         } else {
//             // On desktop, show the desktop sidebar
//             setIsSidebarVisible(true);
//             setIsMobileSidebarOpen(false);
//         }
        
//         // Update the share URL when a point is clicked
//         setTimeout(updateShareUrl, 100);
//     }
// });

// Mobile sidebar component (fullscreen overlay)
const renderMobileSidebarContent = () => {
    if (!sidebarContent || !isMobileSidebarOpen) return null;

    const { properties } = sidebarContent;
    const imageUrls = sidebarContent.images || [];
    
    return (
        <div 
            className="mobile-sidebar-content fade-in" 
                      style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'white',
                zIndex: 1000,
                overflowY: 'auto'
            }}
        >
       
              <button 
                    onClick={() => setIsMobileSidebarOpen(false)}
                    style={{
                        position: 'fixed',
                        top: '10px',
                        right: '10px',
                        background: 'none',
                        border: 'none',
                        fontSize: '30px',
                        cursor: 'pointer',
                        zIndex: 3,
                        padding: '5px',
                        width: '30px',
                        height: '30px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        filter: 'drop-shadow(0 0 4px black)',
                    }}
                >×</button>
            <div style={{ position: 'relative' }}>
                <ImageSlider
                    images={imageUrls}
                    projectType={properties.projectType}
                    cityCardImage={properties.projectType === 'itc' ? city_card :
                                  properties.projectType === 'itc26' ? city2026_card :
                                  properties.projectType === 'oui' ? ouiDesign_card :
                                  properties.projectType === 'pdw' ? parisDesignWeek_card : null}
                />
            </div>
            <PropertiesContainer properties={properties} />
        </div>
    );
};

// Make sure this is included in your render method

    // Render filter section with options - 'All' option is removed
    const renderFilterSection = (title, options, filterType) => {
        // Get the correct meta filter key
        const metaFilterKey = getMetaFilterKey(filterType);
                          
        return (
            <div 
                className="filter-section" 
                style={{
                    marginBottom: '10px',
                    padding: '10px',
                    backgroundColor: '#f0f0f0',
                    borderRadius: '5px',
                    width: '100%'
                    
                }}
            >
                <div 
                    onClick={() => toggleSection(filterType)}
                    style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        cursor: 'pointer',
                        userSelect: 'none'
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center', maxWidth:'60%' }}>
                        <h3 
                            style={{ margin: '0', fontWeight: 'bold', fontSize: isMobile ? '14px' : '16px' }}
                        >
                            {title}
                        </h3>
                        
                    </div>
                        <div style={{ display: 'flex' }}>
                    <SectionHeaderCheckbox 
                            filterType={filterType}
                            isActive={metaFilters[metaFilterKey]}
                            onClick={toggleMetaFilter}
                        />
                    <span 
                        style={{ 
                            transform: expandedSections[filterType] ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s ease',
                            fontSize: '16px',
                            color: 'blue'
                        }}
                    >
                        ▼
                    </span></div>
                </div>
                
                <div style={{ 
                    display: expandedSections[filterType] ? 'block' : 'none',
                    marginTop: '10px'
                }}>
                    <div style={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '5px'
                    }}>
                        {options.map(option => {
                            // Get icon for option
                            const icon = (
                                filterType === 'pointsOfInterest' ? POINT_ICONS[option] :
                                filterType === 'pointsOfSales' ? SALES_ICONS[option] :
                                filterType === 'designEvent' ? (
                                    option === 'In the City' ? DESIGN_ICONS['ITC'] :
                                    option === 'In The City 2026' ? DESIGN_ICONS['ITC26'] :
                                    option === 'Oui Design! 2025' ? DESIGN_ICONS['OUI'] :
                                    option === 'Paris Design Week 2025' ? DESIGN_ICONS['PDW'] : null
                                ) :
                                filterType === 'district' ? null :
                                filterType === 'awards' ? (
                                    option === 'Le FD100 - 1ère édition' ? AWARD_ICONS['FD2019'] :
                                    option === 'Le FD100 - 2ème édition' ? AWARD_ICONS['FD2022'] :
                                    option === 'Le FD100 - 3ème édition' ? AWARD_ICONS['FD2024'] :
                                    option === 'Le FD100 - 4ème édition' ? AWARD_ICONS['FD2025'] : null
                                ) : null
                            );
                            
                            // Check if this option is currently selected
                            const isSelected = selectedFilters[filterType] === option;
                            
                            return (
                                <button 
                                    key={option} 
                                    onClick={() => applyFilters(filterType, option)}
                                    style={{
                                        padding: '5px 10px',
                                        backgroundColor: isSelected ? '#40549e' : '#ffffff',
                                        color: isSelected ? 'white' : 'black',
                                        border: '1px solid #40549e',
                                        borderRadius: '3px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        textAlign: 'left',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        filter: 'none !important',
                                        transition: 'all 0.2s ease',
                                        width: '100%',
                                        justifyContent: filterType === 'district' ? 'flex-start' : 'flex-start',
                                        boxShadow: filterType === 'district' && isSelected ? '0 2px 4px rgba(0,0,0,0.15)' : 'none',
                                    }}
                                >
                                    {icon && (
                                        <img 
                                            src={icon} 
                                            alt={`${option} icon`}
                                            style={{
                                                width: '20px',
                                                height: '20px',
                                                objectFit: 'contain',
                                            }}
                                            className="filter-button-icon"
                                            data-no-filter="true"
                                        />
                                    )}
                                    {filterType === 'district' && (
                                        <span style={{
                                            display: 'inline-block',
                                            width: '20px',
                                            height: '20px',
                                            borderRadius: '50%',
                                            backgroundColor: getAllDistricts().find(d => d.name === option)?.color || '#ccc',
                                            marginRight: '10px',
                                            flexShrink: 0
                                        }} />
                                    )}
                                    <span style={{
                                        textTransform: 'uppercase',
                                        letterSpacing: '2px',
                                        fontWeight: '300',
                                        fontSize: '12px'
                                    }}>
                                        {filterType === 'district' ? getAllDistricts().find(d => d.name === option)?.displayName : formatCategoryForDisplay(option)}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    // Render mobile filter button
const renderMobileFilterButton = () => {
  if (!isMobile) return null;
  
  return (
    <button
      onClick={toggleFilters}
      className="mobile-filter-button"
      style={{
        position: 'absolute',
        bottom: '5%', // Use percentage instead of pixels
        right: '5%', // Use percentage instead of pixels
        zIndex: 5,
        width: '50px',
        height: '50px',
        borderRadius: '50%',
        backgroundColor: '#40549e',
        color: 'white',
        border: 'none',
        boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
        fontSize: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer'
      }}
    >
      <span style={{ fontSize: '28px', position: 'relative' }}>≡</span>
    </button>
  );
};// Render mobile filter panel (slide up from bottom)
    const renderMobileFilterPanel = () => {
        if (!isMobile || !isFilterOpen) return null;
        
        return (
            <div
                className="filter-panel-open" // Add this class            className="filter-panel-open" // Add this class
                style={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    height: '80%',
                    backgroundColor: 'white',
                    zIndex: 10,
                    borderTopLeftRadius: '15px',
                    borderTopRightRadius: '15px',
                    boxShadow: '0 -2px 10px rgba(0,0,0,0.2)',
                    overflowY: 'auto',
                    transition: 'transform 0.3s ease',
                    transform: isFilterOpen ? 'translateY(0)' : 'translateY(100%)'
                }}
            >
                <div style={{ padding: '15px' }}>
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginBottom: '15px',
                        borderBottom: '1px solid #eee',
                        paddingBottom: '10px'
                    }}>
                        <h2 style={{ margin: 0, fontSize: '18px' }}>Filter Options</h2>
                        <button
                            onClick={toggleFilters}
                            style={{
                                background: 'none',
                                border: 'none',
                                fontSize: '24px',
                                cursor: 'pointer',
                                color:'blue'
                            }}
                        >×</button>
                    </div>
                    
                    <button
                        onClick={resetFilters}
                        style={{
                            width: '100%',
                            padding: '12px',
                            marginBottom: '15px',
                            backgroundColor: '#0039d6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            letterSpacing: '1.2px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                    >
                        Clear all filters
                    </button>
                    
                    {renderFilterSection('Points of Interest', POINTS_OF_INTEREST_OPTIONS, 'pointsOfInterest')}
                    {renderFilterSection('Awards', AWARDS_OPTIONS, 'awards')}
                    {renderFilterSection('Design Points of Sales', DESIGN_POINTS_OF_SALES_OPTIONS, 'pointsOfSales')}
                    {renderFilterSection('Design Events', DESIGN_EVENTS_OPTIONS, 'designEvent')}
                    {renderFilterSection('Districts', DISTRICTS_OPTIONS, 'district')}
                </div>
            </div>
        );
    };

    // Responsive CSS styles for the map
    const mapContainerStyle = {
        position: 'relative', 
        width: '100%', 
        height: 'calc(100vh - 95px)',
        borderTop: 'solid 1px', 
        borderBottom: 'solid 1px'
    };

    // Add this component to your file
// First, let's modify the ShareButton component to use relative positioning
const ShareButton = () => {
  const [copied, setCopied] = useState(false);
  
  const copyToClipboard = () => {
    // Force an update of the share URL with current filters
    const filterType = Object.keys(selectedFilters).find(key => selectedFilters[key] !== null);
    const filterValue = filterType ? selectedFilters[filterType] : null;
    
    // Get current map view
    const center = map.current.getCenter();
    const mapView = {
      lat: center.lat,
      lng: center.lng,
      zoom: map.current.getZoom()
    };
    
    // Generate a fresh share URL with current state
    const currentShareUrl = generateShareLink(filterType, filterValue, mapView);
    
    navigator.clipboard.writeText(currentShareUrl)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        setShareUrl(currentShareUrl);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };
  
  return (
    <div 
      className="share-button-container"
      style={{
        position: 'absolute',
        bottom: isMobile ? '20%' : '15%', // Use percentage of container height
        right: '2%', // Use percentage of container width
        zIndex: isMobile ? 5 : 20,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '10px',
        pointerEvents: 'auto'
      }}
    >
      <button
        onClick={copyToClipboard}
        style={{
          backgroundColor: copied ? '#4CAF50' : '#40549e',
          color: 'white',
          border: 'none',
          padding: '10px 15px',
          borderRadius: '4px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '5px',
          boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
          transition: 'background-color 0.3s',
          minWidth: '120px',
          minHeight: '44px',
          fontWeight: 'bold'
        }}
      >
        {copied ? '✓ Copied!' : 'Share this view'}
      </button>
    </div>
  );
};
// Then, add the ShareButton component to your main render method
// Inside the <div ref={mapContainer}> container, add:

    // The updated render method for responsive design
    return (
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
            <AnimatedSection direction="right">
                <SearchBar onSearch={handleSearch} geoJsonData={geoJsonData} />
            </AnimatedSection>
            <AnimatedSection direction="left">
                <div className="design-map-container" style={mapContainerStyle}>
                    <div 
                        ref={mapContainer} 
                        id="map" 
                        style={{ 
                            position: 'absolute', 
                            top: 0, 
                            bottom: 0, 
                            left: 0, 
                            right: 0 
                        }} 
                    >
<ShareButton 
  shareUrl={shareUrl}
  isMobile={isMobile} 
  selectedFilters={selectedFilters}
  mapRef={map}
  generateLink={generateShareLink}
  setShareUrl={setShareUrl}
/>
                        {/* Desktop filter panel */}
                        {!isMobile && (
                            <div 
                                className="filter-container" 
                                style={{
                                    position: 'absolute',
                                    top: '10px',
                                    right: '10px',
                                    backgroundColor: 'white',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                    maxHeight: '80%',
                                    overflowY: 'auto',
                                    zIndex: 1,
                                    width: '300px'
                                }}
                            >
                                <button
                                    onClick={resetFilters}
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        marginBottom: '15px',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        backgroundColor: '#0039d6',
                                        color: 'white',
                                        textTransform: 'uppercase',
                                        letterSpacing: '1.2px',
                                        transition: 'all 0.2s ease',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}
                                    onMouseOver={(e) => {
                                        e.currentTarget.style.backgroundColor = '#002bb3';
                                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                                    }}
                                    onMouseOut={(e) => {
                                        e.currentTarget.style.backgroundColor = '#0039d6';
                                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                    }}
                                >Clear filters</button>
                                {renderFilterSection('Points of Interest', POINTS_OF_INTEREST_OPTIONS, 'pointsOfInterest')}
                                {renderFilterSection('Awards', AWARDS_OPTIONS, 'awards')}
                                {renderFilterSection('Design Points of Sales', DESIGN_POINTS_OF_SALES_OPTIONS, 'pointsOfSales')}
                                {renderFilterSection('Design Events', DESIGN_EVENTS_OPTIONS, 'designEvent')}
                    {renderFilterSection('Districts', DISTRICTS_OPTIONS, 'district')}
                            </div>
                        )}
                        
                        {/* Desktop sidebar */}
                        {!isMobile && renderDesktopSidebarContent()}
                        
                        {/* Mobile filter button */}
                        {renderMobileFilterButton()}
                        
                        {/* Mobile filter panel */}
                        {renderMobileFilterPanel()}
                        
                        {/* Mobile full-screen sidebar */}
                        {renderMobileSidebarContent()}
                    </div>
                </div>
            </AnimatedSection>
            
            {/* Add custom styles for responsive design */}
            <style>
                {`
                @media (max-width: 768px) {
                    .mappopup {
                        max-width: 150px !important;
                    }
                    
                    .mappopup .infocontainer {
                        padding: 5px !important;
                    }
                    
                    .mappopup img {
                        width: 100% !important;
                    }
                    
                    .sidebar-content {
                        width: 90% !important;
                        left: 5% !important;
                    }
                    
                    /* Improve touch targets on mobile */
                    button {
                        min-height: 44px;
                    }
                }
                
                /* Customization for the map popup */
                .mapboxgl-popup {
                    z-index: 3;
                }
                
                .mapboxgl-popup-content {
                    padding: 0 !important;
                    border-radius: 5px;
                    overflow: hidden;
                }
                
                /* Animation for the mobile filter panel */
                @keyframes slideUp {
                    from { transform: translateY(100%); }
                    to { transform: translateY(0); }
                }
                `}
            </style>
        </div>
    );
};

export default DesignMap;